import React from 'react';
import { ChevronDown, ChevronUp, FileText, Paperclip } from 'lucide-react';
import { InboxAttachmentChip } from '../../../inbox/components/InboxAttachmentChip';
import { InboxAttachmentPreviewModal } from '../../../inbox/components/InboxAttachmentPreviewModal';
import {
  buildInboxAttachmentDownloadUrl,
  getInboxAttachmentVisual,
  type InboxAttachmentPreviewTarget,
} from '../../../inbox/inboxAttachmentUtils';
import {
  mailHtmlPreviewClassName,
  prepareMailHtmlForPreview,
  splitMailPlainTextParagraphs,
} from '../../../../utils/mailHtmlPreview';
import {
  parseAttachmentsJson,
  rewriteEmailInlineImages,
  type InlineMailAttachment,
} from '../../../../utils/rewriteEmailInlineImages';
import {
  formatLeadDocumentSize,
  type LeadDocument,
} from '../../../projects/utils/leadDocumentFiles';

/** Collapsed preview height — enough for several lines of the original mail body. */
const COLLAPSED_MAX_HEIGHT_PX = 168;

type MailFileAttachment = InlineMailAttachment & { size?: number | null };

/** Real file attachments — skip inline CID images (signatures/logos). */
function listFileAttachments(raw: unknown): MailFileAttachment[] {
  return parseAttachmentsJson(raw).filter((att) => {
    if (!String(att.filename ?? '').trim()) return false;
    if (String(att.content_id ?? '').trim()) return false;
    return true;
  }) as MailFileAttachment[];
}

function sanitizeLeadMailHtml(html: string): string {
  if (!html) return '';
  // Keep <style> so template/HTML mail layouts survive; dangerous CSS is cleaned in prepareMailHtmlForPreview.
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    .replace(/href="javascript:[^"]*"/gi, 'href="#"')
    .replace(/href='javascript:[^']*'/gi, "href='#'");
}

function escapePlainToHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
}

function LinkedLeadDocumentChip({
  doc,
  onPreview,
}: {
  doc: LeadDocument;
  onPreview: (doc: LeadDocument) => void;
}) {
  const visual = getInboxAttachmentVisual(doc.filename, doc.mime_type ?? null);
  const sizeLabel = formatLeadDocumentSize(doc.size);
  return (
    <button
      type="button"
      onClick={() => onPreview(doc)}
      className="group flex w-full max-w-[min(100%,18rem)] items-center gap-2.5 rounded-xl border border-app-border/90 bg-app-surface px-2.5 py-2 text-left transition hover:border-app-text-subtle/50 hover:bg-app-hover"
      title={doc.filename}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 ${visual.badgeClassName}`}
      >
        {visual.kind === 'pdf' ? (
          <FileText className="h-4 w-4" strokeWidth={1.75} />
        ) : (
          <Paperclip className="h-4 w-4" strokeWidth={1.75} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-app-text">{doc.filename}</p>
        <p className="mt-0.5 text-[10px] text-app-text-subtle">
          {[sizeLabel, 'Vorschau'].filter(Boolean).join(' · ')}
        </p>
      </div>
    </button>
  );
}

export function LeadHistoryEmailBody({
  emailId,
  bodyHtml,
  bodyText,
  snippet,
  hasMoreBody = false,
  accountId,
  externalMessageId,
  attachmentsJson,
  linkedDocuments = [],
  onPreviewDocument,
  isExpanded,
  onToggleExpanded,
}: {
  emailId: string;
  bodyHtml?: string | null;
  bodyText?: string | null;
  snippet?: string | null;
  /** Server flag: the body (not loaded yet) holds more than the snippet. */
  hasMoreBody?: boolean | null;
  accountId?: number | string | null;
  externalMessageId?: string | null;
  attachmentsJson?: string | unknown[] | null;
  linkedDocuments?: LeadDocument[];
  onPreviewDocument?: (doc: LeadDocument) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = React.useState(false);
  const [previewAttachment, setPreviewAttachment] = React.useState<InboxAttachmentPreviewTarget | null>(
    null,
  );

  const allAttachments = React.useMemo(
    () => parseAttachmentsJson(attachmentsJson),
    [attachmentsJson],
  );
  const fileAttachments = React.useMemo(
    () => listFileAttachments(attachmentsJson),
    [attachmentsJson],
  );
  const downloadableAttachments = React.useMemo(
    () => fileAttachments.filter((att) => String(att.id ?? '').trim()),
    [fileAttachments],
  );

  // Prefer linked lead documents (previewable) when present; otherwise inbox attachment chips.
  const useLinkedDocuments = linkedDocuments.length > 0;
  const showInboxAttachments = !useLinkedDocuments && downloadableAttachments.length > 0;
  const hasShownFiles = useLinkedDocuments || showInboxAttachments;

  const rawHtml = React.useMemo(() => {
    const html = (bodyHtml || '').trim();
    if (!html) return '';
    if (accountId == null || !externalMessageId) return html;
    if (allAttachments.length === 0) return html;
    return rewriteEmailInlineImages(html, {
      accountId,
      messageId: String(externalMessageId),
      attachments: allAttachments,
    });
  }, [accountId, allAttachments, bodyHtml, externalMessageId]);

  const rawText = (bodyText || snippet || '').trim();
  const hasHtml = Boolean(rawHtml);
  const preparedHtml = React.useMemo(() => {
    if (!hasHtml) return '';
    return prepareMailHtmlForPreview(sanitizeLeadMailHtml(rawHtml), {
      scopeId: `lead-hist-${emailId}`,
    });
  }, [emailId, hasHtml, rawHtml]);

  const plainParagraphs = React.useMemo(
    () => (hasHtml ? [] : splitMailPlainTextParagraphs(rawText)),
    [hasHtml, rawText],
  );

  const hasBody = hasHtml ? Boolean(preparedHtml.trim()) : plainParagraphs.length > 0;

  const remeasure = React.useCallback(() => {
    const el = contentRef.current;
    if (!el || isExpanded) return;
    setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT_PX + 1);
  }, [isExpanded]);

  React.useLayoutEffect(() => {
    if (isExpanded || !hasBody) return;
    remeasure();
  }, [hasBody, preparedHtml, plainParagraphs, isExpanded, remeasure]);

  React.useLayoutEffect(() => {
    if (isExpanded || !hasBody) return;
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => remeasure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isExpanded, hasBody, remeasure]);

  if (!hasBody && !hasShownFiles) {
    return <div className="mt-3 text-sm text-app-text-subtle">—</div>;
  }

  // The list only ships the snippet; expanding loads the body. Offer the toggle
  // when the server says there is more, even if the snippet itself fits.
  const bodyLoaded = Boolean((bodyHtml || '').trim() || (bodyText || '').trim());
  const bodyPending = !bodyLoaded && Boolean(hasMoreBody);
  const showToggle = hasBody && (overflows || isExpanded || bodyPending);

  return (
    <div className="mt-3 min-w-0">
      {hasBody ? (
        <>
          <div className="relative">
            <div
              ref={contentRef}
              className={isExpanded ? undefined : 'overflow-hidden'}
              style={isExpanded ? undefined : { maxHeight: COLLAPSED_MAX_HEIGHT_PX }}
            >
              {hasHtml ? (
                // Mail HTML assumes a white page: only dark mode keeps it on a white sheet.
                <div className="dark:rounded-lg dark:border dark:border-app-border dark:bg-white dark:p-3">
                  <div
                    className={`${mailHtmlPreviewClassName(rawHtml)} !rounded-none !border-0 !bg-transparent !p-0`}
                    dangerouslySetInnerHTML={{ __html: preparedHtml }}
                  />
                </div>
              ) : (
                <div className="space-y-3 text-sm text-app-text">
                  {plainParagraphs.map((paragraph, idx) => (
                    <p
                      key={idx}
                      className="m-0 whitespace-pre-wrap break-words"
                      dangerouslySetInnerHTML={{ __html: escapePlainToHtml(paragraph) }}
                    />
                  ))}
                </div>
              )}
            </div>
            {!isExpanded && (overflows || bodyPending) ? (
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-x-0 bottom-0 ${
                  overflows ? 'h-14' : 'h-6'
                } rounded-b-lg bg-gradient-to-t to-transparent ${
                  hasHtml ? 'from-white via-white/85' : 'from-app-surface via-app-surface/85'
                }`}
              />
            ) : null}
          </div>
          {showToggle ? (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="mt-1.5 inline-flex items-center gap-0.5 rounded text-xs font-medium text-pipedrive-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
            >
              {isExpanded ? (bodyPending ? 'Wird geladen…' : 'Weniger anzeigen') : 'Alles anzeigen'}
              {isExpanded ? (
                <ChevronUp className="h-3 w-3 shrink-0" aria-hidden />
              ) : (
                <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
              )}
            </button>
          ) : null}
        </>
      ) : null}

      {useLinkedDocuments && onPreviewDocument ? (
        <div className={`${hasBody ? 'mt-3' : ''} flex flex-wrap gap-2`}>
          {linkedDocuments.map((doc) => (
            <LinkedLeadDocumentChip key={doc.id} doc={doc} onPreview={onPreviewDocument} />
          ))}
        </div>
      ) : null}

      {showInboxAttachments ? (
        <div className={`${hasBody ? 'mt-3' : ''} flex flex-wrap gap-2`}>
          {downloadableAttachments.map((att, idx) => {
            const filename = att.filename || 'Anhang';
            const downloadUrl =
              accountId != null && externalMessageId && att.id
                ? buildInboxAttachmentDownloadUrl({
                    accountId,
                    messageId: String(externalMessageId),
                    attachmentId: String(att.id),
                    filename,
                  })
                : null;
            const previewTarget: InboxAttachmentPreviewTarget | null = downloadUrl
              ? {
                  downloadUrl,
                  filename,
                  contentType: att.content_type ?? null,
                }
              : null;
            return (
              <InboxAttachmentChip
                key={`${att.id || filename}-${idx}`}
                filename={filename}
                size={att.size}
                contentType={att.content_type}
                previewTarget={previewTarget}
                onOpen={setPreviewAttachment}
              />
            );
          })}
        </div>
      ) : null}

      <InboxAttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}
